import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import GlitchText from './components/glitch';
import ScrollVelocity from './components/scrolltext';
import TiltedCard from './components/Photo';
import Lanyards from './components/lanyards';

type Experience = {
  role: string;
  company: string;
  period: string;
  summary: string;
  bullets?: string[];
  logo?: string;
  image?: string;
};

type Project = {
  name: string;
  stack: string[];
  description: string;
  meta: string;
  link?: string;
};

type Timeline = {
  title: string;
  org: string;
  period: string;
  detail: string;
  icon?: React.ReactNode;
};

const experiences: Experience[] = [
  {
    role: 'Founding Engineer / Founder',
    company: 'Stealth Startup',
    period: 'Dec 2025 – Jun 2026',
    summary:
      'Building an end-to-end digital platform for a stealth sports entertainment startup, including a React Native mobile app, React admin dashboard, and scalable Node.js/SQL backend.',
    bullets: [
      'Developed mobile app using React Native for player profiles, team management, fixtures, and real-time match updates',
      'Built a responsive React-based admin dashboard for league operations and match management',
      'Designed and deployed a scalable Node.js backend with SQL database schema and optimized REST APIs',
    ],
    logo: '/stealth_logo.jpg',
    image: '/stealth_logo.jpg',
  },
  {
    role: 'SDE Intern',
    company: 'Trent Ltd (Tata)',
    period: 'Sep 2025 – Dec 2025',
    summary:
      'Shipped production features for MyStar (500K+ installs) and ZudioFanz; tightened performance and reliability across mobile flows.',
    bullets: [
      'Implemented Store Locator, profile flows, bill & credit-note loading with caching',
      'Integrated backend services + CleverTap analytics and fixed major QA/UAT bugs',
      'Built OTP auto-detect, global offline handling, and supported MongoDB → SQL migration',
      'Patched Shopify Liquid issues for Burnt Toast & Samoh (cart logic, responsive header, PDP)',
    ],
    logo: '/trent.png',
    image: '/trent.png',
  },
  {
    role: 'Full Stack Developer Intern',
    company: 'Saarthi',
    period: 'Mar 2025 – May 2025',
    summary: 'Worked with the CTO on a Flask admin portal to cut load times and downtime.',
    bullets: [
      'Improved portal load times by 35% and reduced downtime by 25%',
      'Docker + Terraform + AWS SQS + Lambda to automate data scraping pipelines (‑40% manual effort)',
      'Built responsive admin dashboards for data visualization',
    ],
    logo: '/saarthi_logo.jpeg',
    image: '/saarthi_logo.jpeg',
  },
  {
    role: 'App Development Intern',
    company: 'BeeYarn',
    period: 'Jan 2025 – Mar 2025',
    summary: 'Built Flutter app for iOS/Android with a Laravel backend.',
    bullets: [
      'Auth + real-time flows; optimized performance with advanced Flutter concepts',
      'Worked directly with the founder to ship cross-platform releases',
      'Implemented database sync and offline-first architecture',
    ],
    logo: '/beeyarn_logo.jpeg',
    image: '/beeyarn_logo.jpeg',
  },
  {
    role: 'SDE Intern',
    company: 'AnyFeast',
    period: 'Sep 2024 – Dec 2024',
    summary: 'Shipped deep-linking and responsive web screens to boost engagement.',
    bullets: [
      'Deep Linking in Flutter app (50% better sharing, 15% higher engagement)',
      'Built multiple React + TypeScript screens from Figma for the marketing site',
      'Optimized recipe navigation UX through interactive filtering',
    ],
    logo: '/anyfeast_logo.png',
    image: '/anyfeast_logo.png',
  },
];

const projects: Project[] = [
  {
    name: 'RAG Codebase Assistant & AI Git Bot',
    stack: ['Node.js', 'TypeScript', 'Qdrant', 'OpenAI', 'Claude API', 'GitHub API'],
    description:
      'AI assistant for scanning GitHub repos with function-level chunking and vector embeddings, answering queries with file & line citations in under 2 seconds. Includes a GitHub Actions bot for PR reviews, bug detection, and release notes generation.',
    meta: 'AI • Vector Search • Developer Tools',
    link: 'https://github.com/whoaryu/rag-git-bot',
  },
  {
    name: 'Distributed Feature Flag System',
    stack: ['Node.js', 'TypeScript', 'PostgreSQL', 'Redis', 'React', 'SSE'],
    description:
      'High-performance, LaunchDarkly-like feature flagging system with under 5ms evaluation latency. Implements deterministic user bucketing using MurmurHash, Server-Sent Events (SSE) streaming updates, targeting rules, audit logs, and scheduled rollouts.',
    meta: 'System Design • Real-time • Scaling',
    link: 'https://github.com/whoaryu/feature-flag-system',
  },
  {
    name: 'LikhAI — Collaborative AI Writing Copilot',
    stack: ['TypeScript', 'React', 'Node.js', 'MongoDB', 'OpenAI'],
    description:
      'AI-powered writing assistant with real-time collaboration, document drafting, version control, poetic analysis, and AI-driven content enhancement for writers and filmmakers.',
    meta: 'AI • Collaboration • Content',
    link: 'https://likh-ai.vercel.app',
  },
  {
    name: 'Safar — AI Travel Itinerary Planner',
    stack: ['Flutter', 'React', 'Node.js', 'FastAPI', 'MongoDB', 'Gemini AI'],
    description:
      'Cross-platform travel planner with Google Gemini AI integration. Features natural language itinerary generation, budget tracking, day-wise maps, influencer-curated trips, and offline access.',
    meta: 'Mobile + Web • AI • Travel',
    link: 'https://safar-swart.vercel.app',
  },
  {
    name: 'Orbit — Developer 1v1 Video Chat',
    stack: ['React', 'TypeScript', 'WebRTC', 'Node.js', 'WebSockets'],
    description:
      'Developer-matching video chat room utilizing WebRTC peer-to-peer streaming with STUN/TURN traversal and WebSockets for real-time signaling.',
    meta: 'Real-time • P2P • Video',
    link: 'https://github.com/whoaryu/orbit',
  },
  {
    name: 'Saathi — End-to-End Pet Care Ecosystem',
    stack: ['Flutter', 'Node.js', 'MongoDB', 'Cloudinary', 'JWT'],
    description:
      'Pet adoption mobile platform featuring a Tinder-style swipe interface, AI-powered pet care chatbot, 30+ service booking slots, and a curated e-commerce pet store.',
    meta: 'Mobile • Backend • Commerce',
    link: 'https://github.com/whoaryu/saathi',
  },
];

const timelines: Timeline[] = [
  {
    title: 'Finalist - DJCSI Codeshastra XI',
    org: 'Top 12 of 60 (2000+ regs)',
    period: '2025',
    detail: 'Built and pitched in a competitive hackathon setting.',
    icon: <img src="/hackathon.jpg" alt="DJCSI Finalist" className="timeline-logo" />,
  },
  {
    title: 'OC VP Marketing - AIESEC Mumbai',
    org: 'Saathi Campaigns',
    period: '2024',
    detail: 'Led campaigns, generated 250+ participants, maintained lead pipelines.',
    icon: <img src="/aiesec_logo.jpg" alt="AIESEC Mumbai" className="timeline-logo" />,
  },
  {
    title: 'Buildspace S5 Graduate',
    org: 'Nights and Weekends Program',
    period: '2024',
    detail: 'Completed 6-week intensive product building with weekly milestones.',
    icon: <img src="/buildspace.png" alt="Buildspace" className="timeline-logo" />,
  },
];

const blogSnippets: any[] = [
  {
    id: 'deep-linking-delight',
    title: 'Making deep linking actually delightful on mobile',
    tag: 'Mobile • DX',
    summary: 'Notes from shipping deep-linking on Flutter and React Native without breaking existing flows.',
    date: 'Jan 2025',
  }
]

const skills = {
  languages: ['C++', 'Java', 'Dart', 'JavaScript', 'TypeScript', 'Python', 'C', 'SQL', 'HTML', 'CSS', 'Bash'],
  technologies: [
    'Flutter',
    'React.js',
    'React Native',
    'Tailwind',
    'Node.js',
    'MongoDB',
    'Firebase',
    'Docker',
    'Flask',
    'Terraform',
    'AWS (Lambda, SQS, CloudWatch)',
    'LocalStack',
    'GitHub Actions',
    'CleverTap',
  ],
  concepts: ['Deep Linking', 'State Management (GetX, Provider, Bloc)', 'Event-Driven Systems', 'Infrastructure as Code'],
};

function App() {
  const [expandedExp, setExpandedExp] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<Record<string, boolean>>({});
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current.filter(Boolean);
    sections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  return (
    <div className="app-shell">
      <Lanyards position={[0, 0, 15]} gravity={[0, -40, 0]} />
      <header className="hero">
        <div className="hero__left">
          <div className="eyebrow">Software Engineer • Full Stack • Mobile</div>
          <GlitchText
            speed={0.8}
            enableShadows={true}
            enableOnHover={true}
            className="hero__title"
          >
            Aryan Shah
          </GlitchText>
          <p className="hero__subtitle">
            I build reliable mobile and web experiences that stay fast under real-world traffic. Currently crafting
            production features for consumer apps and automating data-heavy backends.
          </p>
          <div className="hero__meta">
            <span>📍 Mumbai, India</span>
            <span>☎️ +91 8976247365</span>
            <a href="mailto:aryanpshah1@gmail.com">aryanpshah1@gmail.com</a>
          </div>
          <div className="hero__links">
            <a href="https://github.com/whoaryu" className="chip" target="_blank" rel="noreferrer">GitHub</a>
            <a href="https://linkedin.com/in/aryanpshah1" className="chip" target="_blank" rel="noreferrer">LinkedIn</a>
            <a href="https://leetcode.com/u/whoaryu/" className="chip" target="_blank" rel="noreferrer">LeetCode</a>
            <a href="https://bit.ly/aryanshah-resume" className="chip chip--primary">My Resume</a>
          </div>
        </div>
        <div className="hero__right">
          {isMobile ? (
            <TiltedCard
              imageSrc="/profile_picture.png"
              altText="Aryan Shah"
              containerHeight="300px"
              containerWidth="300px"
              imageHeight="300px"
              imageWidth="300px"
              rotateAmplitude={12}
              scaleOnHover={1.2}
              showMobileWarning={false}
              showTooltip={true}
            />
          ) : (
            <div className="hero__right-placeholder"></div>
          )}
        </div>
      </header>

      <section
        id="experience"
        className="section section--fade"
        ref={(el) => { sectionsRef.current[0] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Work Experience</p>
          <h2>Shipping work that reaches users</h2>
        </div>
        <div className="exp-list">
          {experiences.map((exp, idx) => (
            <article
              key={exp.company}
              className={`exp-card ${expandedExp === exp.company ? 'exp-card--expanded' : ''}`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <button
                className="exp-card__header"
                onClick={() => setExpandedExp(expandedExp === exp.company ? null : exp.company)}
              >
                <div className="exp-card__logo-container">
                  {exp.logo && !logoError[exp.company] ? (
                    <img
                      src={exp.logo}
                      alt={exp.company}
                      className="exp-card__logo"
                      onError={() => setLogoError(prev => ({ ...prev, [exp.company]: true }))}
                    />
                  ) : (
                    <div className="exp-card__logo-fallback">
                      {exp.company.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="exp-card__info">
                  <div className="exp-card__top">
                    <div>
                      <h3>{exp.role}</h3>
                      <p className="muted">{exp.company}</p>
                    </div>
                    <span className="pill pill--ghost">{exp.period}</span>
                  </div>
                  <p className="summary">{exp.summary}</p>
                </div>
                <span className="expand-icon">{expandedExp === exp.company ? '▼' : '▶'}</span>
              </button>

              {expandedExp === exp.company && (
                <div className="exp-card__details">
                  {exp.bullets && (
                    <ul className="bullet-list">
                      {exp.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <section
        id="education"
        className="section section--fade"
        ref={(el) => { sectionsRef.current[1] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Education</p>
          <h2>Foundations</h2>
        </div>
        <div className="card">
          <div className="card__row">
            <div className="logo-circle">🎓</div>
            <span className="pill">2022 – Present</span>
          </div>
          <h3>Dwarkadas Jivanlal Sanghvi College of Engineering</h3>
          <p className="muted">B.Tech in Computer Engineering • CGPA: 8.34/10 • Mumbai, Maharashtra</p>
        </div>
      </section>

      <section
        id="skills"
        className="section section--fade"
        ref={(el) => { sectionsRef.current[2] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Skills</p>
          <h2>Toolkit at a glance</h2>
        </div>
        <div className="scroll-velocity-wrapper">
          <div className="scroll-velocity-section">
            <p className="scroll-velocity-label">Languages</p>
            <ScrollVelocity
              texts={[skills.languages.join(' • ')]}
              velocity={50}
              damping={50}
              stiffness={400}
              numCopies={6}
              className="scroll-velocity-text"
              parallaxClassName="scroll-velocity-parallax"
              scrollerClassName="scroll-velocity-scroller"
              velocityMapping={{ input: [0, 1000], output: [0, 3] }}
            />
          </div>
          <div className="scroll-velocity-section">
            <p className="scroll-velocity-label">Technologies</p>
            <ScrollVelocity
              texts={[skills.technologies.join(' • ')]}
              velocity={-50}
              damping={50}
              stiffness={400}
              numCopies={6}
              className="scroll-velocity-text"
              parallaxClassName="scroll-velocity-parallax"
              scrollerClassName="scroll-velocity-scroller"
              velocityMapping={{ input: [0, 1000], output: [0, 3] }}
            />
          </div>


          <div className="scroll-velocity-section">
            <p className="scroll-velocity-label">Concepts</p>
            <ScrollVelocity
              texts={[skills.concepts.join(' • ')]}
              velocity={50}
              damping={50}
              stiffness={400}
              numCopies={6}
              className="scroll-velocity-text"
              parallaxClassName="scroll-velocity-parallax"
              scrollerClassName="scroll-velocity-scroller"
              velocityMapping={{ input: [0, 1000], output: [0, 3] }}
            />
          </div>
        </div>
      </section>

      <section
        id="projects"
        className="section section--fade"
        ref={(el) => { sectionsRef.current[3] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Proof of Work</p>
          <GlitchText
            speed={1.2}
            enableShadows={true}
            enableOnHover={true}
            className="section__glitch-title"
          >
            Projects that reflect how I build
          </GlitchText>
        </div>
        <div className="cards-grid cards-grid--projects">
          {projects.map((project, idx) => (
            <a
              key={project.name}
              href={project.link || '#'}
              target={project.link ? '_blank' : '_self'}
              rel={project.link ? 'noreferrer' : ''}
              className={`card ${!project.link ? 'card--no-link' : ''}`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <p className="eyebrow" style={{ marginBottom: '8px' }}>{project.meta}</p>
              <div className="card__row">
                <h3>{project.name}</h3>
                {project.link && <span className="link-arrow">↗</span>}
              </div>
              <p className="summary">{project.description}</p>
              <div className="tag-row">
                {project.stack.map((tech) => (
                  <span key={tech} className="tag tag--ghost">{tech}</span>
                ))}
              </div>
            </a>
          ))}
        </div>
      </section>

      <section
        id="timeline"
        className="section section--fade"
        ref={(el) => { sectionsRef.current[4] = el; }}
      >
        <div className="section__head">
          <p className="eyebrow">Additional Experience</p>
          <h2>Hackathons & Leadership</h2>
        </div>
        <div className="timeline-compact">
          {timelines.map((item, idx) => (
            <div key={item.title} className="timeline-item" style={{ animationDelay: `${idx * 80}ms` }}>
              <span className="timeline-item__icon">{item.icon}</span>
              <div className="timeline-item__content">
                <h4>{item.title}</h4>
                <p className="muted">{item.org} • {item.period}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section
        id="contact"
        className="section contact section--fade"
        ref={(el) => { sectionsRef.current[5] = el; }}
      >
        <div>
          <p className="eyebrow">Contact</p>
          <h2>Let's build something users love</h2>
          <p className="summary">
            Open to roles where product reliability, performance, and shipping fast matter. Happy to share more work samples
            or dive into architecture.
          </p>
        </div>
        <div className="contact__actions">
          <a className="btn" href="mailto:aryanpshah1@gmail.com">Email me</a>
          <a className="btn btn--ghost" href="https://github.com/whoaryu" target="_blank" rel="noreferrer">GitHub</a>
          <a className="btn btn--ghost" href="https://linkedin.com/aryanpshah1" target="_blank" rel="noreferrer">LinkedIn</a>
        </div>
      </section>
    </div>
  );
}

export default App;
